/** 使用同一份真实资料生成文本；保留原始附录，未记录字段不补猜。 */
import { employmentRecordText, employmentText } from './sbemployment.ts';
import { historySourceLabel, historySourceNote } from './sbhistory.ts';
import { medicalRecordText, medicalText } from './sbmedical-export.ts';
import { secondaryCompletionText } from './sbeducation.ts';
import { clinicianCareerText } from './sbclinical-career.ts';
import { deathText } from './sbdeath.ts';
import { monthSnapshotsText } from './sbexport-months.ts';
import { liquidBridgeText } from './sbexport-liquid-bridge.ts';
import { currentLoanText } from './sbexport-loans.ts';
import { portfolioEntryText, portfolioScopeText } from './sbexport-portfolio.ts';
type Data = Record<string, any>;
const date = (d: number) => `第 ${Math.floor(d / 360) + 1} 年 ${Math.floor(d % 360 / 30) + 1} 月 ${d % 30 + 1} 日`;
const money = (v: unknown) => typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(2)} 元` : '未知';
const fen = (v: unknown) => typeof v === 'number' ? money(v / 100) : '未知';
const known = (v: unknown) => v == null || v === '' ? '未知' : String(v);

/** 家庭和监护(导出那天的样子)。 */
function familySnapshotText(value: unknown): string[] {
  const object = (v: unknown): Data | null => v != null && typeof v === 'object' && !Array.isArray(v) ? v as Data : null;
  const text = (v: unknown) => typeof v === 'string' && v.trim() !== '' ? v : '未记录';
  const id = (v: unknown) => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= 0xffffffff ? v : null;
  const person = (v: unknown) => {
    const r = object(v), n = r?.kind === 'person' ? id(r.id) : null;
    return `${text(r?.name)} · ${n == null ? '人物编号未记录' : `人物 #${n}`}`;
  };
  const member = (v: unknown) => {
    const r = object(v), age = r?.age;
    return `${person(r?.who)} · 关系 ${text(r?.rel)} · 年龄 ${typeof age === 'number' && Number.isFinite(age) && age >= 0 ? age : '未记录'} 岁 · 性别 ${text(r?.sex)}` +
      ` · 生存状态 ${r?.alive === true ? '在世' : r?.alive === false ? '已去世' : '未记录'} · 在城 ${r?.here === true ? '是' : r?.here === false ? '否' : '未记录'} · 羁押 ${r?.held === true ? '是' : r?.held === false ? '否' : '未记录'}`;
  };
  const lines = ['家庭与监护（导出那天）'];
  const f = object(value);
  if (!f) return [...lines, '未记录'];
  const day = id(f.day);
  lines.push(`家庭快照日：${day == null ? '未记录' : date(day)}`);
  const members = (label: string, rows: unknown) => {
    if (!Array.isArray(rows)) lines.push(`${label}：未记录`);
    else if (!rows.length) lines.push(`${label}：无`);
    else { lines.push(`${label}：`); for (const r of rows) lines.push(`  ${member(r)}`); }
  };
  members('登记父母', f.parents);
  lines.push(`当前生活监护人：${f.guardian === null ? '当前未登记' : object(f.guardian) ? member(f.guardian) : '未记录'}`);
  members('登记子女与受照料孩子', f.children);
  members('登记同住成员', f.household);
  const home = id(f.home);
  lines.push(`当前登记住房：${f.home === null ? '当前无登记住所' : home == null ? '未记录' : `房屋 #${home}`}`,
    `买口粮的人：${f.foodBuyer === null ? '没有' : object(f.foodBuyer) ? person(f.foodBuyer) : '未记录'}`,
    `家人接济：${f.helpDaily === true ? '每天看' : f.helpDaily === false ? '每月看' : '未记录'}`);
  return lines;
}

/** 同一份正文顺序；累计每64次遍历访问让步。单条helper/最终stringify与join仍同步。 */
export function* lifeTextSteps(x: Data): Generator<void, string, unknown> {
  const p = x.data ?? {}, a = p.audit ?? {}, scope = x.archive ?? {}, records: Data[] = x.history ?? [];
  if (!Array.isArray(records)) throw new Error('人物归档记录格式无效：需要数组');
  let visited = 0;
  const tags = new Map<number, Data>((scope.metadata?.tags ?? scope.tags ?? []).map((t: Data) => [t.id, t]));
  const lines: string[] = [`${x.name} · 人生档案`, `人物 #${x.id} · 城市种子 ${x.seed} · 导出于 ${date(x.day)}`, '', '资料范围',
    x.recordScope ?? '', scope.note ?? '归档范围未提供。', a.scope ?? '本人资料，不能自动视为全家合并账。',
    '文本金额统一换算为元；原始附录按元/分字典保留精度。月度快照覆盖整期，不能归因于某一件事。', '', '人物基本资料',
    `姓名 ${known(p.name)} · 性别 ${known(p.sex)} · 当前年龄 ${known(p.age)} 岁 · 成年线 ${known(a.adultAge)} 岁`,
    `职业 ${p.job ? `${p.job.firm?.name ?? ''} · ${p.job.trade ?? ''}` : p.firms?.some((f: Data) => f.boss) ? '经营公司，见公司资料' : '当前无雇员岗位'}`,
    `手艺 ${known(p.skill)} · 工种 ${known(p.trade)} · 配偶 ${p.spouse?.who?.name ?? p.spouse?.name ?? '当前无配偶'}`,
    `实际监护的未成年子女 ${known(a.householdDependents)} 人 · 符合劳动在场条件 ${a.workAvailable == null ? '未知' : a.workAvailable ? '是' : '否'}`,
    '', ...familySnapshotText(p.familyDetails),
    '', '资金与统计口径', `手头现钱 ${money(a.cashNow)} · 名下存款 ${money(a.deposit)} · 应收欠薪 ${money(a.wageReceivable)}`,
    a.liquidNote ?? '', a.receivableNote ?? '', a.repayScope ?? '', a.consumptionScope ?? '',
    `终身现金与存款账差 ${money(a.liquidLifetimeDifference)}`, '', '任职与工资（真实观察范围）', ...employmentText(p.employment),
    '', ...deathText(p.death, { here: p.here, goneHow: p.gone?.how }), '', ...medicalText(p.medical), '', ...secondaryCompletionText(p.education), '', ...clinicianCareerText(p.education?.academic), '', '人物来历（实际创建或迁入记录）'];
  const origins: Data[] = [];
  for (const r of records) {
    if (r.source === 'origin' && r.kind === 'person' && r.entityId === x.id) origins.push(r);
    if (++visited % 64 === 0) yield;
  }
  if (!origins.length) lines.push('此档案未保存可用的结构化来历；不能把最早一条普通事件当成来历。');
  for (const r of origins) {
    lines.push(`${date(r.day)} · 事件 #${r.seq}`, JSON.stringify(r.data, null, 2));
    if (++visited % 64 === 0) yield;
  }
  lines.push('', '当前住房与贷款（唯一编号）');
  for (const h of p.houses ?? []) {
    lines.push(`房屋 #${known(h.id)} · 地块 #${h.plot} · ${h.building} · ${h.area}㎡ · 买入支付 ${money(h.bought)} · 当前估值 ${money(h.value)} · ${h.self ? '自住' : h.tenant ? `租给 ${h.tenant.name}` : '空置'} · 合同日租 ${money(h.rent)}`);
    if (++visited % 64 === 0) yield;
  }
  for (const l of p.loans ?? []) {
    lines.push(...currentLoanText(l));
    if (++visited % 64 === 0) yield;
  }
  lines.push('', ...portfolioScopeText(p.portfolio, x.day));
  if (Array.isArray(p.portfolio)) for (let i = 0; i < p.portfolio.length; i++) {
    lines.push(...portfolioEntryText(p.portfolio[i], i, x.day));
    if (++visited % 64 === 0) yield;
  }
  lines.push('', '月度资产负债与实际收付');
  for (const r of records) {
    if (r.source === 'resident_period') {
      const d = r.data ?? {}, m = d.period ?? {}, b = m.balance ?? {};
      lines.push(`\n${date(m.day ?? r.day)} 结账 · 记录 #${r.seq}`, `期末现金 ${fen(b.cash)} · 存款 ${fen(b.deposit)} · 房产 ${money(b.property)} · 登记股份 ${money(b.shares)} · 其他经营权益 ${money(b.business)} · 土地 ${money(b.land)} · 期房 ${money(b.presales)} · 在建权益 ${money(b.construction)} · 贷款本金 ${fen(b.debt)} · 净资产 ${money(b.netWorth)}`,
        `本期实际总流入 ${fen(m.totalIn)} · 实际总流出 ${fen(m.totalOut)}（排除本人存取款；含资产买卖和借还，不等同收入/消费）`);
      if (d.opening?.kind === 'previous_closing') lines.push(`期初来自真实 ${date(d.opening.day)} 结账：净资产 ${money(d.opening.balance?.netWorth)} · 现金 ${fen(d.opening.balance?.cash)} · 存款 ${fen(d.opening.balance?.deposit)}`);
      else if (d.opening?.kind === 'observation') lines.push(`期初参考实际观察 ${date(d.opening.day)}，原始记录 #${d.opening.refSeq}；观察前的本月流水不冒充观察后的变化。`);
      else lines.push('期初资产快照缺失，不反推。');
      if (!d.completeFlows) lines.push('本期仅保留旧版主要分类摘要，完整分类未知。');
      for (const side of ['in', 'out']) {
        lines.push(side === 'in' ? '逐类到账：' : '逐类支付：');
        for (const f of d.flows?.[side] ?? []) { const t = tags.get(f.tag); lines.push(`  ${t?.[side === 'in' ? 'inName' : 'outName'] ?? `流水类别 #${f.tag}`}：${fen(f.amount)}${f.internal ? '（本人存取款，不计收入/消费）' : ''}`); }
      }
      lines.push(...liquidBridgeText(r, x, scope.metadata));
    }
    if (++visited % 64 === 0) yield;
  }
  lines.push('', ...monthSnapshotsText(a));
  lines.push('', '按发生顺序的全部已归档事件', '同一事件编号关联人物与公司时是一条共同记录，不能再相加为两笔收入；任职报价不是实领，旧雇主补欠不算新岗位工资。');
  const sources = new Set<string>();
  for (const r of records) {
    sources.add(r.source);
    if (++visited % 64 === 0) yield;
  }
  for (const source of sources) {
    lines.push(`${historySourceLabel(source)} [${source}]：${historySourceNote(source)}`);
    if (++visited % 64 === 0) yield;
  }
  for (const r of records) {
    const d = r.data ?? {};
    lines.push(`\n${date(r.day)} · #${r.seq} · ${historySourceLabel(r.source)}${r.source !== 'employment' && (d.event || d.name) ? ` · ${d.event ?? d.name}` : ''} · ${r.kind} #${r.entityId}`);
    if (r.source === 'medical') lines.push(...medicalRecordText(d));
    else if (r.source === 'death') lines.push(...deathText({ observed: !!d.death, record: d.death, scope: historySourceNote('death') }, { alive: false }));
    else if (typeof d.detail === 'string') lines.push(d.detail);
    if (r.source === 'employment') lines.push(...employmentRecordText(d, r.day));
    if (r.source === 'property_sale') lines.push(`房屋 #${d.unit} · 地块 #${d.plot} · 成交总价 ${fen(d.gross)} · 清贷本金 ${fen(d.principalRepaid)} · 卖家实际到账 ${fen(d.netReceived)} · 广告实际支付 ${fen(d.advertisingPaid)} · 交割对账 ${d.settlementComplete ? '相符' : '尚未对齐'}`);
    if (r.source === 'ordinary') lines.push(`实际行动者 #${d.actor} · 对象 #${d.target ?? '无'} · 行动者 ${d.actorHours} 小时 · 对象 ${d.targetHours} 小时`, `当时依据：${d.reason}`, `结果：${d.result}`);
    if (!['life', 'ordinary', 'resident_period', 'employment', 'medical'].includes(r.source)) lines.push(JSON.stringify(d, null, 2));
    if (++visited % 64 === 0) yield;
  }
  // 最终整包序列化前允许取消；它本身及最终join不会被分块。
  yield;
  lines.push('', '未记录或无法恢复的资料', ...(a.missing ?? []), '', '原始完整资料附录（保留字段、编号与原始单位）', JSON.stringify(x, null, 2));
  return lines.join('\n');
}

/** 保留原同步返回string契约；下载异步路径消费同一个生成器，不维护第二套正文。 */
export function lifeText(x: Data): string {
  const steps = lifeTextSteps(x);
  for (;;) { const next = steps.next(); if (next.done) return next.value; }
}
